import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { CheckIcon, ExclamationCircleIcon } from '@heroicons/react/outline';
import { spacing } from '../../../design-system/tokens';
import OutlookOAuthButton from '../../Settings/OAuth/OutlookOAuthButton';
import { GmailOAuthButton } from '../../Settings/OAuth';
import { channelService } from '../../../services/channelService';
import { OutlookIcon, GmailIcon } from '../../../design-system/components/data-display/EmailProviderIcons';

interface EmailConnectProps {
  onConnectionChange: (connected: boolean) => void;
}

const Container = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const Title = styled.h2`
  font-size: 32px;
  font-weight: 600;
  color: #222429;
  margin: 0 0 12px 0;
  line-height: 1.25;
  letter-spacing: -0.025em;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #64748b;
  margin: 0 0 ${spacing[10]} 0;
  line-height: 1.5;
  font-weight: 400;
`;

const EmailProviders = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const ProviderItem = styled.div<{ connected: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing[5]} ${spacing[4]};
  border: 1px solid ${props => 
    props.connected ? '#10b981' : 
    props.disabled ? '#e2e8f0' : '#e2e8f0'
  };
  border-radius: 8px;
  background: ${props => 
    props.connected ? '#f0fdf4' : 
    props.disabled ? '#f8fafc' : '#ffffff'
  };
  opacity: ${props => props.disabled ? 0.6 : 1};
  transition: all 0.2s ease;
  
  &:hover {
    ${props => !props.disabled && `
      border-color: ${props.connected ? '#10b981' : '#cbd5e1'};
      background: ${props.connected ? '#f0fdf4' : '#f8fafc'};
    `}
  }
`;

const ProviderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
`;

const ProviderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
`;

const ProviderDetails = styled.div``;

const ProviderName = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: #222429;
  margin-bottom: 4px;
`;

const ProviderStatus = styled.div`
  font-size: 14px;
  color: #64748b;
`;

const ConnectedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #059669;
  font-size: 13px;
  font-weight: 500;
`;

const DisabledBadge = styled.div`
  color: #94a3b8;
  font-size: 13px;
  font-weight: 400;
`;

const WarningMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[2]};
  padding: ${spacing[3]};
  margin-top: ${spacing[3]};
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 6px;
  font-size: 13px;
  color: #92400e;
  line-height: 1.5;
`;

const WarningIcon = styled.div`
  flex-shrink: 0;
  margin-top: 2px;
`;

export const EmailConnect: React.FC<EmailConnectProps> = ({ onConnectionChange }) => {
  const [outlookChannel, setOutlookChannel] = useState<any>(null);
  const [gmailChannel, setGmailChannel] = useState<any>(null);

  useEffect(() => {
    loadChannels();
  }, []);

  useEffect(() => {
    onConnectionChange(!!outlookChannel || !!gmailChannel);
  }, [outlookChannel, gmailChannel, onConnectionChange]);

  const loadChannels = async () => {
    try {
      const channels = await channelService.getAllChannels();
      const outlook = channels.find((ch: any) => ch.channel_subtype === 'outlook');
      const gmail = channels.find((ch: any) => ch.channel_subtype === 'gmail');
      setOutlookChannel(outlook);
      setGmailChannel(gmail);
    } catch (error) {
      console.error('Error loading channels:', error);
    }
  };

  const handleChannelConnected = async () => {
    await loadChannels();
  };

  return (
    <Container>
      <Title>Connect your email</Title>
      <Subtitle>
        Connect your email account to start managing your work with creator.
      </Subtitle>

      <EmailProviders>
        <ProviderItem connected={!!outlookChannel}>
          <ProviderInfo>
            <ProviderIcon>
              <OutlookIcon size={24} />
            </ProviderIcon>
            <ProviderDetails>
              <ProviderName>Microsoft Outlook</ProviderName>
              <ProviderStatus>
                {outlookChannel ? outlookChannel.display_name : 'Connect your Outlook account'}
              </ProviderStatus>
            </ProviderDetails>
          </ProviderInfo>
          {outlookChannel ? (
            <ConnectedBadge>
              <CheckIcon style={{ width: '12px', height: '12px' }} />
              Connected
            </ConnectedBadge>
          ) : (
            <OutlookOAuthButton onSuccess={handleChannelConnected} />
          )}
        </ProviderItem>

        <ProviderItem connected={!!gmailChannel}>
          <ProviderInfo>
            <ProviderIcon>
              <GmailIcon size={24} />
            </ProviderIcon>
            <ProviderDetails>
              <ProviderName>Gmail</ProviderName>
              <ProviderStatus>
                {gmailChannel ? gmailChannel.display_name : 'Connect your Gmail account'}
              </ProviderStatus>
            </ProviderDetails>
          </ProviderInfo>
          {gmailChannel ? (
            <ConnectedBadge>
              <CheckIcon style={{ width: '12px', height: '12px' }} />
              Connected
            </ConnectedBadge>
          ) : (
            <GmailOAuthButton onSuccess={handleChannelConnected} ownershipType="personal" />
          )}
        </ProviderItem>
        
        {!gmailChannel && (
          <WarningMessage>
            <WarningIcon>
              <ExclamationCircleIcon style={{ width: '16px', height: '16px' }} />
            </WarningIcon>
            <div>
              <strong>Important:</strong> Before connecting your Gmail account, please inform Adly about the email address you intend to connect. This ensures proper setup and access.
            </div>
          </WarningMessage>
        )}
      </EmailProviders>
    </Container>
  );
};
